// pages/components/support/FAQ.jsx
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, MessageSquare, Search } from 'lucide-react';

const FAQ = ({ compact = false, onCreateTicket }) => {
const [openIndex, setOpenIndex] = useState(null);
const [searchTerm, setSearchTerm] = useState('');

const faqs = [
{
category: 'Getting Started',
questions: [
{
question: 'How do I create an account?',
answer: 'Click on the "Sign Up" button in the top right corner, fill in your details, and verify your email address. Once verified, you can start using the platform immediately.'
},
{
question: 'What subscription plans are available?',
answer: 'We offer four plans: Freemium (free, 5 job matches/month), Standard ($29/month, 25 matches), Premium ($59/month, 100 matches), and Elite ($99/month, unlimited matches). Each plan includes different features.'
},
{
question: 'How does job matching work?',
answer: 'Our AI algorithm analyzes your resume, skills, and preferences to match you with relevant job opportunities. You\'ll receive notifications when new matches are found.'
}
]
},
{
category: 'Account & Billing',
questions: [
{
question: 'How do I upgrade my subscription?',
answer: 'Go to Billing & Subscription page, select "Plans & Pricing", choose your desired plan, and click "Upgrade Now". Your new features will be available immediately.'
},
{
question: 'Can I cancel my subscription anytime?',
answer: 'Yes, you can cancel anytime from the Billing page. You\'ll retain access until the end of your current billing cycle. No refunds for partial months.'
},
{
question: 'What payment methods do you accept?',
answer: 'We accept all major credit cards (Visa, Mastercard, American Express), debit cards, and PayPal. All payments are processed securely through our payment gateway.'
}
]
},
{
category: 'Job Applications',
questions: [
{
question: 'How does auto-apply work?',
answer: 'When enabled, our system automatically submits your profile to matched jobs that meet your criteria. You can enable/disable this feature in Settings > Preferences.'
},
{
question: 'Can I track my applications?',
answer: 'Yes! Go to the Applications page to view all your submitted applications, their status, and any responses from employers.'
},
{
question: 'How many jobs can I apply to?',
answer: 'Your monthly job match limit depends on your subscription plan. However, you can manually apply to unlimited jobs beyond your matched quota.'
}
]
},
{
category: 'Technical Issues',
questions: [
{
question: 'I forgot my password. What should I do?',
answer: 'Click "Forgot Password" on the login page, enter your email, and we\'ll send you a reset link. The link expires in 1 hour for security.'
},
{
question: 'My resume upload failed. Why?',
answer: 'Make sure your resume is in PDF, DOC, or DOCX format and under 5MB. Check your internet connection and try again. If the issue persists, contact support.'
},
{
question: 'Why am I not receiving email notifications?',
answer: 'Check your spam/junk folder. Add noreply@yourplatform.com to your contacts. You can also adjust notification settings in Settings > Preferences.'
}
]
}
];

const filteredFAQs = faqs.map(category => ({
...category,
questions: category.questions.filter(q =>
q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
q.answer.toLowerCase().includes(searchTerm.toLowerCase())
)
})).filter(category => category.questions.length > 0);

const toggleQuestion = (index) => {
setOpenIndex(openIndex === index ? null : index);
};

return (
<div className="space-y-6">
<div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8 shadow-sm">
<div className="mb-6">
<h2 className="text-2xl font-bold text-slate-900 mb-2">
Frequently Asked Questions
</h2>
<p className="text-slate-600">
Find quick answers to common questions
</p>
</div>

{/* Search */}
{!compact && (
<div className="mb-6">
<div className="relative">
<Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
<input
type="text"
value={searchTerm}
onChange={(e) => setSearchTerm(e.target.value)}
placeholder="Search FAQs..."
className="w-full pl-12 pr-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
/>
</div>
</div>
)}

{/* FAQ Categories */}
<div className="space-y-6">
{filteredFAQs.map((category, categoryIndex) => (
<div key={categoryIndex}>
{!compact && (
<h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
<span className="w-1 h-6 bg-blue-600 rounded-full"></span>
{category.category}
</h3>
)}

<div className="space-y-2">
{category.questions.slice(0, compact ? 3 : undefined).map((faq, questionIndex) => {
const globalIndex = `${categoryIndex}-${questionIndex}`;
const isOpen = openIndex === globalIndex;

return (
<div
key={questionIndex}
className="border border-slate-200 rounded-lg overflow-hidden hover:border-blue-300 transition-all"
>
<button
onClick={() => toggleQuestion(globalIndex)}
className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-slate-50 transition-colors"
>
<span className="font-semibold text-slate-900 pr-4">
  {faq.question}
</span>
{isOpen ? (
  <ChevronUp className="flex-shrink-0 text-blue-600" size={20} />
) : (
  <ChevronDown className="flex-shrink-0 text-slate-400" size={20} />
)}
</button>

{isOpen && (
<div className="px-4 pb-4 bg-slate-50 border-t border-slate-200">
  <p className="text-slate-700 leading-relaxed pt-4">
    {faq.answer}
  </p>
</div>
)}
</div>
);
})}
</div>
</div>
))}
</div>

{/* No Results */}
{searchTerm && filteredFAQs.length === 0 && (
<div className="text-center py-8">
<Search className="mx-auto text-slate-300 mb-3" size={48} />
<p className="text-slate-600">No FAQs found matching "{searchTerm}"</p>
</div>
)}

{/* Still Need Help */}
<div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
<div className="flex flex-col md:flex-row items-center justify-between gap-4">
<div className="flex items-start gap-3">
<MessageSquare className="text-blue-600 flex-shrink-0 mt-0.5" size={24} />
<div>
<h4 className="font-bold text-slate-900 mb-1">Still need help?</h4>
<p className="text-sm text-slate-600">
Can't find what you're looking for? Create a support ticket and our team will assist you.
</p>
</div>
</div>

<button
onClick={onCreateTicket}
className="flex-shrink-0 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold rounded-lg shadow-md transition-all"
>
Create Ticket
</button>
</div>
</div>
</div>
</div>
);
};

export default FAQ;